
import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signUp: (email: string, password: string, fullName?: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    console.log('🔐 Setting up auth state listener');
    
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        
        
        // Handle successful authentication
        if (event === 'SIGNED_IN' && session) {
          
          setSession(session);
          setUser(session.user);
          setLoading(false);
        } 
        // Handle sign out
        else if (event === 'SIGNED_OUT') {
          console.log('👋 User signed out');
          setSession(null);
          setUser(null);
          setLoading(false);
        }
        // Handle token refresh
        else if (event === 'TOKEN_REFRESHED' && session) {
          
          setSession(session);
          setUser(session.user);
          setLoading(false);
        }
        // Handle initial load or any other state
        else {
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);
        }
      }
    );

    // THEN check for existing session. Trust whatever is in localStorage —
    // the onAuthStateChange listener above will fire INITIAL_SESSION with
    // the same data on its own, so this is a second-chance setter only.
    //
    // The earlier version of this block called supabase.auth.getUser() here
    // to validate the token server-side. That extra round trip produced two
    // additional setState bursts during mount and (combined with downstream
    // effects reacting to each burst) drove React error #185 / Maximum
    // update depth exceeded on /dashboard. If a token is genuinely stale,
    // the next real API call returns 401 and we handle it at that point.
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('❌ Error getting session:', error);
      } else {
        console.log('📋 Initial session check:', session?.user?.email || 'No session');
      }
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => {
      
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, fullName?: string) => {
    console.log('📝 Attempting to sign up:', email);
    const redirectUrl = `${window.location.origin}/auth/callback`;
    
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName
          }
        }
      });
      
      console.log('📊 Sign up response:', { data, error });
      
      if (error) {
        console.error('❌ Sign up error:', error.message, error);
      } else {
        console.log('✅ Sign up successful for:', email);
      }
      
      return { error };
    } catch (err) {
      console.error('❌ Sign up exception:', err);
      return { error: err };
    }
  };

  const signIn = async (email: string, password: string) => {
    console.log('🔑 Attempting to sign in:', email);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      console.log('📊 Sign in response:', { data, error });
      
      if (error) {
        console.error('❌ Sign in error:', error.message, error);
      } else {
        console.log('✅ Sign in successful for:', email);
      }
      
      return { error };
    } catch (err) {
      console.error('❌ Sign in exception:', err);
      return { error: err };
    }
  };

  const signOut = async () => {
    console.log('👋 Signing out user');
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('❌ Sign out error:', error);
        // Don't throw - let the auth state change handle the cleanup
      }
      // Always clear local state regardless of server response
      setSession(null);
      setUser(null);
    } catch (error) {
      console.error('❌ Sign out exception:', error);
      // Clear local state even if server call fails
      setSession(null);
      setUser(null);
    }
  };

  const resetPassword = async (email: string) => {
    console.log('🔄 AuthContext - Starting password reset for:', email);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth?reset=true`,
      });
      
      if (error) {
        console.error('❌ AuthContext - Password reset error:', error);
        return { error };
      }
      
      console.log('✅ AuthContext - Password reset email sent successfully');
      return { error: null };
    } catch (error) {
      console.error('❌ AuthContext - Unexpected password reset error:', error);
      return { error };
    }
  };

  const value = {
    user,
    session,
    signUp,
    signIn,
    signOut,
    resetPassword,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
